import { createClient } from '@supabase/supabase-js'
import { requireSupabaseConfig } from './runtime'

export interface MedicineCategoryRow {
  id: number
  pharmacy_type: string
  category_id: string
  name: string
  icon: string
  sort_order: number
}

export interface ListFrequencyOptionRow {
  id: string
  label: string
  per_week: number
  sort_order: number
  is_default: boolean
}

export interface TimePerListOptionRow {
  id: string
  label: string
  hours: number
  sort_order: number
  is_default: boolean
}

export interface SyncFrequencyRow {
  id: string
  label: string
  description: string
  hint: string | null
  interval_minutes: number | null
  recommended_for: string[]
  sort_order: number
}

export interface OnboardingSlideRow {
  id: string
  sort_order: number
  icon: string
  color: string
  title: string
  description: string
}

export interface PricingTierRow {
  id: string
  name: string
  tagline: string | null
  branch_range: string | null
  base_usd_rate: number | null
  prices: Record<string, number>
  volume_discounts: Array<{ minBranches: number; maxBranches: number; usdRate: number }>
  limits: Record<string, unknown>
  features: string[]
  is_popular: boolean
  sort_order: number
}

export interface PPPMultiplierRow {
  id: string
  country_name: string
  multiplier: number
  currency_code: string
}

export interface FXRateRow {
  id: string
  base_currency: string
  target_currency: string
  rate: number
  last_updated: string
}

export interface AIMessageTemplateRow {
  id: string
  category: string
  key: string
  template: string
  variables: string[]
}

export interface SettingsMenuRow {
  id: string
  parent_id: string | null
  sort_order: number
  key: string
  label: string
  description: string | null
  icon: string | null
  route: string | null
  requires_permission: string | null
}

let cachedClient: ReturnType<typeof createClient> | null = null
let cachedToken: string | undefined

function getClient(authToken?: string) {
  if (cachedClient && cachedToken === authToken) return cachedClient
  const config = requireSupabaseConfig()
  cachedClient = createClient(config.url, config.anonKey, {
    global: authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined,
  })
  cachedToken = authToken
  return cachedClient
}

export async function fetchMedicineCategories(authToken: string): Promise<MedicineCategoryRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('onboarding_medicine_categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchSyncFrequencies(authToken: string): Promise<SyncFrequencyRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('sync_frequencies')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchListFrequencyOptions(authToken: string): Promise<ListFrequencyOptionRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('onboarding_list_frequency_options')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchTimePerListOptions(authToken: string): Promise<TimePerListOptionRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('onboarding_time_per_list_options')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchOnboardingSlides(authToken: string, locale = 'en'): Promise<OnboardingSlideRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('onboarding_slides')
    .select('*')
    .eq('is_active', true)
    .eq('locale', locale)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchPricingTiers(authToken: string): Promise<PricingTierRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('pricing_tiers')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

export async function fetchPPPMultipliers(authToken: string): Promise<PPPMultiplierRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('ppp_multipliers')
    .select('*')
    .eq('is_active', true)
  if (error) throw error
  return data || []
}

export async function fetchFXRates(authToken: string): Promise<FXRateRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('fx_rates')
    .select('*')
  if (error) throw error
  return data || []
}

export async function fetchAIMessageTemplates(authToken: string, locale = 'en'): Promise<AIMessageTemplateRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('ai_message_templates')
    .select('*')
    .eq('is_active', true)
    .eq('locale', locale)
  if (error) throw error
  return data || []
}

export async function fetchSettingsMenu(authToken: string): Promise<SettingsMenuRow[]> {
  const supabase = getClient(authToken)
  const { data, error } = await supabase
    .from('settings_menu')
    .select('*')
    .eq('is_active', true)
    .order('sort_order')
  if (error) throw error
  return data || []
}

// pharmacy_types, roles, permissions, role_permissions: removed — data is hardcoded in
// packages/core/src/pharmacy-defaults/ and packages/core/src/permissions.ts
